'use client';

import { useState, useEffect } from 'react';
import { CircularTestimonials } from './ui/circular-testimonials';
import { getImageUrl } from '@/lib/imageUrl';
import { getApiUrl } from "@/lib/apiUrl";

interface ApiTestimonial {
  id: number;
  client_name: string;
  client_designation: string;
  client_company: string;
  description: string;
  title: string;
  image_1: string | null;
  display_pages: string | null;
}

const FALLBACK_TESTIMONIALS = [
  {
    quote: "Our business website went from outdated to cutting-edge in no time. The team understood our vision and delivered a seamless, fast, and beautifully designed site. Highly recommended!",
    name: "Ganapathi",
    designation: "CEO & Founder, Magizh Handicrafts",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/magizh.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["business-website"]
  },
  {
    quote: "I recently got my website developed by Vilva Business, and I must say, their service was outstanding! From the initial consultation to the final launch, the team was highly professional, responsive, and creative.",
    name: "Yuvraj",
    designation: "CEO & Founder, Zemdrop",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/zemdrop.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["business-website", "custom-software"]
  },
  {
    quote: "A game-changer for our online presence. The design is sleek, and the functionality is top-notch! The support team was responsive, and the project was delivered on time with great attention to detail.",
    name: "Ram Dhanapal",
    designation: "CEO & Founder, Ayyawins",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/ram.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["business-website"]
  },
  {
    quote: "I give Vilva D2C a 5/5 rating and have already recommended them to family and friends. I will continue to recommend them to everyone. Technical team is very supportive and fulfill our requirements.",
    name: "Manikandan",
    designation: "CEO & Founder, Adyarhandicrafts",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/adyar.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["vilvad2c"]
  },
  {
    quote: "Vilva Team is building my E-Commerce website and getting great support, understanding my requirements. I see good progress with timely updates. Satisfied with their amazing service.",
    name: "Ananda",
    designation: "CEO & Founder, A & G",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/ag.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["vilvad2c"]
  },
  {
    quote: "Vilva Networks offers 24/7 support with accountability, monitoring, and prompt technical assistance. Their proactive approach and attention to detail make them a reliable tech partner.",
    name: "Sivasamy",
    designation: "CEO & Founder, Nalsshoppy",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/siva.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["vilvad2c", "business-website"]
  },
  {
    quote: "The development of the Thaithingal website has experienced significant growth over the last decade, thanks to Vilva Networks. Their expertise and ongoing support were key to our success.",
    name: "Manikandan",
    designation: "CEO & Founder, Thaithingal",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/thaithingal.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["business-website"]
  },
  {
    quote: "Vilva Network is building my e commerce website ad getting a great support to understand my exact requirements. They are providing me with regular update in timely manner. Wishing you to continue provide the excellent support with same team spirit.",
    name: "Oliver Fernandes",
    designation: "CEO & Founder, Jonas International",
    src: getImageUrl("/vilvabusiness/website/common/testimonials/jonsInternatinal.webp") || "/images/testimonials/default-avatar.webp",
    pages: ["vilvad2c"]
  }
];

export default function CircularTestimonialView({ page = "business-website" }: { page?: string }) {
  // Seed with the page-filtered fallback and start NOT loading, so the testimonials render in the
  // server HTML immediately (crawlers / AEO engines see this social proof). The API fetch below is
  // a client-side enhancement that swaps in live data when it succeeds.
  const initialTestimonials = FALLBACK_TESTIMONIALS.filter((item) => !page || item.pages.includes(page));
  const [testimonials, setTestimonials] = useState<any[]>(initialTestimonials);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTestimonials = async () => {
      let dataFetched = false;
      try {
        const response = await fetch(
          getApiUrl(`/v1/testimonials?display_page=${page}`),
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (response.ok) {
          const result = await response.json();

          if (result.status && Array.isArray(result.data)) {
            const filtered = result.data.filter((item: ApiTestimonial) => {
              if (!page) return true;
              if (!item.display_pages) return true;
              try {
                const pages: string[] = JSON.parse(item.display_pages);
                return pages.includes(page);
              } catch {
                return false;
              }
            });

            const formatted = filtered.map((item: ApiTestimonial) => {
              const apiBaseUrl = getApiUrl("").replace(/\/api\/?$/, "/");
              const imagePath = item.image_1
                ? (item.image_1.startsWith('http') ? item.image_1 : `${apiBaseUrl}${item.image_1.startsWith('/') ? item.image_1.slice(1) : item.image_1}`)
                : "/images/testimonials/default-avatar.webp";

              return {
                quote: item.description,
                name: item.client_name,
                designation: `${item.client_designation}, ${item.client_company}`,
                src: imagePath,
              };
            });

            if (formatted.length > 0) {
              setTestimonials(formatted);
              dataFetched = true;
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch testimonials, using fallback:", error);
      } finally {
        if (!dataFetched) {
          const filteredFallback = FALLBACK_TESTIMONIALS.filter((item) => {
            if (!page) return true;
            return item.pages.includes(page);
          });
          setTestimonials(filteredFallback);
        }
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [page]);

  if (testimonials.length === 0) return null;

  return (
    <div className="w-full flex justify-center items-center">
      <CircularTestimonials
        testimonials={testimonials}
        autoplay={true}
        colors={{
          name: "#090D28",
          designation: "#454545",
          testimony: "#171717",
          arrowBackground: "#1586FF",
          arrowForeground: "#ffffff",
          arrowHoverBackground: "#003FDF",
        }}
        fontSizes={{
          name: "24px",
          designation: "16px",
          quote: "18px",
        }}
      />
    </div>
  );
}
